import { ResponseModel } from "services/ApiClient/responseModel";
import RequestInterceptor from "./requestInterceptor";
import { RequestOptions } from "./requestOptions";
import { NetworkingError } from "./networkingError";

function isJson(inputString: string) {
  try {
    JSON.parse(inputString);
    return true;
  } catch {
    return false;
  }
}

class NetworkingClient {
  baseUrl: URL;

  interceptors: RequestInterceptor[] = [];

  constructor(baseUrl: URL) {
    this.baseUrl = baseUrl;
  }

  async get<T>(path: string, signal?: AbortSignal, cache?: RequestCache): Promise<T> {
    const requestOptions: RequestOptions = {
      method: "GET",
      cache,
    };
    const result = await this.performRequest<ResponseModel<T>>(path, requestOptions, signal);
    return result.payload.data;
  }

  async post<T>(path: string, body: object, signal?: AbortSignal): Promise<T> {
    const requestOptions: RequestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
    return (await this.performRequest<ResponseModel<T>>(path, requestOptions, signal)).payload.data;
  }

  async put<T>(path: string, body: object, signal?: AbortSignal): Promise<T> {
    const requestOptions: RequestOptions = {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
    return (await this.performRequest<ResponseModel<T>>(path, requestOptions, signal)).payload.data;
  }

  // PUT replaces the whole resource; PATCH applies a partial update. Use this
  // method when the backend merges the payload into existing state rather than
  // overwriting it (e.g. task_grid_preferences).
  async patch<T>(path: string, body: object, signal?: AbortSignal): Promise<T> {
    const requestOptions: RequestOptions = {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };
    return (await this.performRequest<ResponseModel<T>>(path, requestOptions, signal)).payload.data;
  }

  async delete<T>(path: string, signal?: AbortSignal): Promise<T> {
    const requestOptions: RequestOptions = {
      method: "DELETE",
    };
    return (await this.performRequest<ResponseModel<T>>(path, requestOptions, signal)).payload.data;
  }

  /// If you need access to the response status code, or your API returns something other than the
  /// ResponseModel interface, you can use this method instead of the convenience methods above.
  async performRequest<T>(
    path: string,
    requestOptions?: RequestOptions,
    signal?: AbortSignal,
  ): Promise<{ payload: T; status: number }> {
    const requestOpts: RequestOptions = requestOptions || { method: "GET" };
    const headers = requestOpts.headers || {};

    const { url, options } = this.interceptors.reduce<{ url: URL; options: RequestOptions }>(
      (partial, interceptor) => interceptor.request(partial.url, partial.options),
      { url: this.url(path), options: { ...requestOpts, headers } },
    );

    // Extract cache option if present
    const fetchOptions: RequestInit = { ...options, signal };
    if (options.cache) {
      fetchOptions.cache = options.cache;
    }

    return fetch(url, fetchOptions).then(async (response: Response) => {
      const text = await response.text();
      // checks for if it is JSON because rails will send back HTML responses at times
      const data = text && isJson(text) && JSON.parse(text);
      if (!response.ok) {
        // Handle both singular 'error' and plural 'errors' from backend
        let errors: string[];
        if (data?.errors) {
          errors = Array.isArray(data.errors) ? data.errors : [data.errors];
        } else if (data?.error) {
          errors = [data.error];
        } else {
          errors = [response.statusText];
        }
        const error: NetworkingError = { errors, status: response.status, data };
        if (response.status === 401) {
          localStorage.removeItem("user-storage");
        }
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(error);
      }
      return { payload: data, status: response.status };
    });
  }

  private url(...pathComponents: string[]): URL {
    const path = pathComponents
      .filter(Boolean)
      .reduce(
        (partial, next) => `${partial}/${next.replace(/^\//, "")}`,
        this.baseUrl.pathname.replace(/^\/+|\/+$/g, ""),
      );

    return new URL(path, this.baseUrl);
  }
}

export default NetworkingClient;
